import { find, get, orderBy } from 'lodash';
import {
  HIRES_FORMAT_SLUG,
  MEDIA_PATH,
  PROXY_FORMAT_SLUG,
  SHARED_MEDIA_PATH,
  THUMBNAIL_FORMAT_SLUG,
  VIDEO,
} from 'src/constants';

/**
 * Format node from GraphQL response
 */
interface FormatNode {
  node: {
    id: string;
    fmtType: {
      slug: string;
    };
    [key: string]: unknown;
  };
}

/**
 * Format set structure from GraphQL response
 */
interface FormatSet {
  edges: FormatNode[];
}

/**
 * Custom field definition from GraphQL
 */
interface CustomFieldDefinition {
  id: string;
  name: string;
  isEditable: boolean;
  fieldType: string;
  fieldChoices?: string[];
  choices?: string[];
}

/**
 * Custom field node from GraphQL response
 */
interface CustomFieldNode {
  node: {
    id: string;
    content: string;
    definition: {
      id: string;
    };
  };
}

/**
 * Custom field set structure from GraphQL response
 */
interface CustomFieldSet {
  edges: CustomFieldNode[];
}

/**
 * View field node from view model
 */
interface ViewFieldNode {
  node: {
    fieldType: 'CUSTOM' | 'NATIVE' | 'FORMAT';
    fieldLabel: string;
    sequence: number;
    field: CustomFieldDefinition;
    id: string;
  };
}

/**
 * View field set structure from view model
 */
interface ViewFieldSet {
  edges: ViewFieldNode[];
}

/**
 * View model structure
 */
interface ViewModel {
  node: {
    viewfieldSet: ViewFieldSet;
  };
}

/**
 * Asset with format set
 */
interface AssetWithFormats {
  formatSet: FormatSet;
  customfieldSet?: CustomFieldSet;
  [key: string]: unknown;
}

/**
 * Asset with generated media URLs
 */
interface AssetWithUrls extends AssetWithFormats {
  assetThumbnailFormat: string | null;
  assetProxyFormat: string | null;
  mediaType: string;
}

/**
 * Collection item node structure
 */
interface CollectionItemNode {
  node?: {
    contentObject?: AssetWithFormats;
  };
}

/**
 * View model component field structure
 */
export interface ViewModelField {
  id: string;
  titleArgument: string;
  title: string;
  value: string | unknown;
  sequence: number;
  isEditable: boolean;
  fieldType: string;
  fieldChoices?: string[];
  customFieldId?: string | null;
  nativeField: boolean;
  choices?: string[];
}

/**
 * Generates media URLs for an asset based on its format set
 *
 * @param asset - The asset containing format information
 * @param sharedLink - Whether this is for a shared link (uses different path)
 * @returns Asset with generated thumbnail and proxy URLs
 */
export const generateMediaUrls = (asset: AssetWithFormats, sharedLink = false): AssetWithUrls => {
  const newAsset = { ...asset } as AssetWithUrls;
  const formats = newAsset.formatSet.edges;

  if (formats.length) {
    const thumbnail = formats.filter((f) => f.node.fmtType.slug === THUMBNAIL_FORMAT_SLUG);
    const thumbnailNode = thumbnail.length ? thumbnail[0] : null;
    const proxyFormat = formats.filter((f) => f.node.fmtType.slug === PROXY_FORMAT_SLUG);
    const proxyNode = proxyFormat.length ? proxyFormat[0] : null;

    if (sharedLink) {
      newAsset.assetThumbnailFormat = thumbnailNode ? `${SHARED_MEDIA_PATH}/` + `${thumbnailNode.node.id}` : null;
      newAsset.assetProxyFormat = proxyNode ? `${SHARED_MEDIA_PATH}/` + `${proxyNode.node.id}` : null;
    } else {
      newAsset.assetThumbnailFormat = thumbnailNode ? `${MEDIA_PATH}/` + `${thumbnailNode.node.id}` : null;
      newAsset.assetProxyFormat = proxyNode ? `${MEDIA_PATH}/` + `${proxyNode.node.id}` : null;
    }
    newAsset.mediaType = VIDEO;
  }
  return newAsset;
};

/**
 * Generates a media URL for a specific type and ID
 *
 * @param type - The base path type
 * @param id - The resource ID
 * @returns The formatted URL
 */
export const genMediaUrl = (type: string, id: string): string => {
  return `${type}/` + `${id}`;
};

/**
 * Generates custom fields with media URLs for a collection of items
 *
 * @param items - Array of collection items or assets
 * @param sharedLink - Whether these are for shared links
 * @returns Array of assets with generated media URLs
 */
export const generateCustomFields = (
  items: (CollectionItemNode | AssetWithFormats)[],
  sharedLink = false,
): AssetWithUrls[] => {
  return items.map((item) => {
    const collectionItem = item as CollectionItemNode;
    const asset = collectionItem.node?.contentObject ? collectionItem.node.contentObject : (item as AssetWithFormats);
    return generateMediaUrls(asset, sharedLink);
  });
};

/**
 * Returns a list of all fields associated with the asset that are also specified in the viewModel
 *
 * @param asset - The asset object
 * @param viewModel - The view model configuration
 * @param replaceMissing - Whether to add default values for missing fields
 * @returns Ordered list of view model fields
 */
export const filterOrderCustomFieldsByViewModel = (
  asset: AssetWithFormats,
  viewModel: ViewModel | null,
  replaceMissing = false,
): ViewModelField[] => {
  const viewModelComponent: ViewModelField[] = [];
  const customFields = asset.customfieldSet;

  viewModel?.node?.viewfieldSet?.edges?.forEach((field) => {
    if (field?.node?.fieldType === 'CUSTOM') {
      if (customFields) {
        let customField: CustomFieldNode | null = null;
        for (let i = 0; i < customFields.edges.length; i++) {
          customField = customFields.edges[i];
          if (customField.node.definition.id === field.node.field.id) {
            viewModelComponent.push({
              titleArgument: field.node.field.name,
              title: field.node.fieldLabel,
              value: customField.node.content,
              sequence: field.node.sequence,
              id: field.node.field.id,
              isEditable: field.node.field.isEditable,
              fieldType: field.node.field.fieldType,
              fieldChoices: field.node.field.fieldChoices,
              customFieldId: customField.node.id,
              nativeField: false,
            });
            break;
          }
          // if replaceMissing is true, add some default "missing" value to the viewModelComponent
          if (i === customFields.edges.length - 1 && replaceMissing) {
            viewModelComponent.push({
              titleArgument: field.node.field.name,
              title: field.node.fieldLabel,
              value: '-',
              sequence: field.node.sequence,
              id: field.node.field.id,
              isEditable: field.node.field.isEditable,
              fieldType: field.node.field.fieldType,
              fieldChoices: field.node.field.fieldChoices,
              customFieldId: null,
              nativeField: false,
            });
          }
        }
      }
    } else if (field.node.fieldType === 'NATIVE') {
      Object.keys(asset).forEach((key) => {
        if (key !== '__typename' && key.toLowerCase().includes(field.node.field.name)) {
          viewModelComponent.push({
            id: field.node.field.id,
            titleArgument: field.node.field.name,
            title: field.node.fieldLabel,
            value: (asset as Record<string, unknown>)[key],
            sequence: field.node.sequence,
            isEditable: field.node.field.isEditable,
            fieldType: field.node.field.fieldType,
            fieldChoices: field.node.field.fieldChoices,
            nativeField: true,
          });
        }
      });
    } else if (field.node.fieldType === 'FORMAT') {
      const edges = get(asset, 'formatSet.edges', []) as FormatNode[];
      const node = find(edges, (x) => x.node.fmtType.slug === HIRES_FORMAT_SLUG);

      const value = get(node, `node.${field.node.field.name}`);

      viewModelComponent.push({
        id: field.node.field.id,
        titleArgument: field.node.field.name,
        title: field.node.fieldLabel,
        value: value,
        sequence: field.node.sequence,
        isEditable: field.node.field.isEditable,
        fieldType: field.node.field.fieldType,
        fieldChoices: field.node.field.fieldChoices,
        nativeField: true,
      });
    }
  });
  return orderBy(viewModelComponent, ['sequence'], ['asc']);
};

/**
 * Returns a list of all fields present on the viewModel, with any corresponding values from the asset
 *
 * @param asset - The asset object
 * @param viewModel - The view model configuration
 * @returns Ordered list of view model fields
 */
export const filterOrderViewModelFields = (asset: AssetWithFormats, viewModel: ViewModel): ViewModelField[] => {
  let viewModelComponent: ViewModelField[] = [];
  const customFields = get(asset, 'customfieldSet.edges', []) as CustomFieldNode[];

  viewModel.node.viewfieldSet.edges.forEach((field) => {
    if (field.node.fieldType === 'CUSTOM') {
      viewModelComponent = [
        ...viewModelComponent,
        {
          titleArgument: field.node.field.name,
          title: field.node.fieldLabel,
          value: customFields
            .filter((customField) => customField.node.definition.id === field.node.field.id)
            .reduce((a, b, i) => a + (i !== 0 ? '|' : '') + b.node.content, ''),
          sequence: field.node.sequence,
          id: field.node.field.id,
          isEditable: field.node.field.isEditable,
          fieldType: field.node.field.fieldType,
          fieldChoices: field.node.field.fieldChoices,
          customFieldId: field.node.id,
          nativeField: false,
          choices: field.node.field.choices,
        },
      ];
    } else if (field.node.fieldType === 'NATIVE') {
      const filteredKey = find(Object.keys(asset), (key) => {
        return key.toLowerCase() === field?.node?.field?.name?.toLowerCase();
      });
      const value = filteredKey ? get(asset, filteredKey) : '';
      viewModelComponent.push({
        id: field.node.field.id,
        titleArgument: field.node.field.name,
        title: field.node.fieldLabel,
        value: value,
        sequence: field.node.sequence,
        isEditable: field.node.field.isEditable,
        fieldType: field.node.field.fieldType,
        fieldChoices: field.node.field.fieldChoices,
        nativeField: true,
      });
    } else if (field.node.fieldType === 'FORMAT') {
      const edges = get(asset, 'formatSet.edges', []) as FormatNode[];
      const node = find(edges, (x) => x.node.fmtType.slug === HIRES_FORMAT_SLUG);
      const value = get(node, `node.${field.node.field.name}`, '');

      viewModelComponent.push({
        id: field.node.field.id,
        titleArgument: field.node.field.name,
        title: field.node.fieldLabel,
        value: value,
        sequence: field.node.sequence,
        isEditable: field.node.field.isEditable,
        fieldType: field.node.field.fieldType,
        fieldChoices: field.node.field.fieldChoices,
        nativeField: true,
      });
    }
  });
  return orderBy(viewModelComponent, ['sequence'], ['asc']);
};

/**
 * Extracts custom field names from a view model
 *
 * @param viewModel - The view model configuration
 * @returns Array of custom field names
 */
export const getCustomFieldNamesFromViewModel = (viewModel: ViewModel | null): string[] => {
  if (viewModel === null) return [];

  const viewFieldSetEdges = viewModel.node ? viewModel.node.viewfieldSet.edges : [];

  const customFields = viewFieldSetEdges.filter((edge) => edge.node.fieldType === 'CUSTOM');

  return customFields.map((edge) => edge.node.field.name);
};
